import { injectable, inject } from 'inversify';
import Debug from 'debug'
import lunr from 'lunr'
import { LunrEnvironment, SerializedSearchIndex, registerHunspellIfRequired,
  registerDefaultHunspellStemmer, loadIndex, createSearch, SearchResultItem, SerializedIndexEntry, LunrEnvironmentContext } from '@xlearn/common';
import aspectStoreModule from '@/app/aspect/apsect-state';
import { HunspellService, HunspellServiceSymbol } from '@/app/hunspell';

export interface SearchResult {

  code: SearchResultCode
  items: SearchResultItem[]
}

export { SearchResultItem, SearchResultPosition } from '@xlearn/common'

type SearchResultCode = 'ok' | 'missing-index'

const debug = Debug('search-service')

@injectable()
export class SearchService {
  private entries: SerializedIndexEntry[];
  private index: lunr.Index;

  private currentAspectId: string|null = null; // TODO: is is temporary HACK\

  private lunrEnvironmentContext?: LunrEnvironmentContext

  public constructor(
    @inject(HunspellServiceSymbol)
    private hunspellService: HunspellService) {}

  public async search(query: string): Promise<SearchResult> {
    debug('search', query)
    
    const search = await this.initIndexIfNeeded();
    
    if (!this.index) {
      debug('there is no index, skipping')

      return {
        code: 'missing-index',
        items: []
      }
    }

    const items = search(query)

    debug('search result', items)

    return { code: 'ok',
             items };
  }

  private async initIndexIfNeeded() {
    const newInstanceId = aspectStoreModule.selectedAspectInstanceId

    if ((!this.entries || !this.index || this.currentAspectId !== newInstanceId)
          && aspectStoreModule.selectedCourseAspectInstance !== null) {

      debug(`loading index, current aspect: ${this.currentAspectId}; new aspect: ${newInstanceId}`)
      this.currentAspectId = newInstanceId
      const language = aspectStoreModule.selectedCourseAspectInstance.language
      const index = aspectStoreModule.selectedCourseAspectInstance.search

      await this.initializeHunspell(language)
      this.setSearchEntriesAndIndex(index);
    }

    return createSearch(this.index, this.entries)
  }

  private async initializeHunspell(language: string) {
    debug('initializing hunspell', language)

    registerDefaultHunspellStemmer() // in case of hunspell could not loaded
    await this.hunspellService.prepareLanguages([language])

    const resolveResult = this.hunspellService.resolveLanguage(language)
    debug('hunspell result', resolveResult)
    if (resolveResult.success) {
      // register hunspell stemmer in the lunr
      const environment = new LunrEnvironment(this.hunspellService);
      if (this.lunrEnvironmentContext) {
        this.lunrEnvironmentContext.dispose()
      }

      const resolvedEnvironmentContext = environment.resolveLanguageEnvironment(language)
      this.lunrEnvironmentContext = resolvedEnvironmentContext
      registerHunspellIfRequired(resolvedEnvironmentContext.details)
    }
  }

  // TODO: not called
  stop() {
    if (this.lunrEnvironmentContext) {
      this.lunrEnvironmentContext.dispose()
      this.lunrEnvironmentContext = undefined
    }
  }

  private setSearchEntriesAndIndex(deserialized: SerializedSearchIndex): void {
    debug('loading index')
    this.entries = deserialized.entries;
    this.index = loadIndex(deserialized.lunrIndex);
    debug('index loaded')
  }
}

export const SearchServiceSymbol = Symbol.for('SearchService');
